import { memo, useEffect, useMemo, useState } from 'react'
import Api from '../../api/endPoints'
import { mapPointsToTableRows } from '../../api/bmsHistoryMapper'
import { buildCsvBlob, downloadBlob, escapeCsvCell, CSV_CONFIRM_THRESHOLD } from '../../utils/csv'
import { parseLabelUnit, headerLabel } from '../../utils/columns'
import { Card } from './BmsCardPrimitives'
import Pagination from '../../components/Pagination'
import ColumnSelector from '../../components/ColumnSelector'
import Spinner from '../../components/Spinner'
import LoadingOverlay from '../../components/LoadingOverlay'


const onOffText = (key) => (row) => row[key] ?? undefined

const onOffRender = (key) => (row) => {
  const value = row[key]
  if (value == null) return undefined
  return <span style={{ color: value === 'ON' ? '#0ca30c' : '#616a79' }}>{value}</span>
}

const TIMESTAMP_COLUMN = { key: 'timestamp', label: 'Timestamp', width: '220px' }

// Plain-text formatters — the source of truth for CSV export.
const TEXT_BY_KEY = {
  powerMode: onOffText('powerMode'),
  batteryMode: onOffText('batteryMode'),
  'fetStatus.chg_on': onOffText('fetStatus.chg_on'),
  'fetStatus.dsg_on': onOffText('fetStatus.dsg_on'),
  'fetStatus.pchg_on': onOffText('fetStatus.pchg_on'),
}

const RENDER_BY_KEY = {
  powerMode: onOffRender('powerMode'),
  batteryMode: onOffRender('batteryMode'),
  'fetStatus.chg_on': onOffRender('fetStatus.chg_on'),
  'fetStatus.dsg_on': onOffRender('fetStatus.dsg_on'),
  'fetStatus.pchg_on': onOffRender('fetStatus.pchg_on'),
}

const LABEL_OVERRIDES = {
  ...Object.fromEntries(Array.from({ length: 7 }, (_, i) => [`ntc${i + 1}`, `Temperature ${i + 1}`])),
  'fetStatus.pchg_on': 'Precharge MOSFET',
  'fetStatus.chg_on': 'Charge MOSFET',
  'fetStatus.dsg_on': 'Discharge MOSFET',
}

// API labels usually spell the unit out already, but not for every key, so
// these fill the gaps: a header must never be unitless while its values are.
const UNIT_BY_KEY = {
  soc_pct: '%',
  soh_pct: '%',
  current_A: 'A',
  afeTemp_C: '°C',
  boardTemp_C: '°C',
  mosfetTemp_C: '°C',
  prechargeTemp_C: '°C',
  balancingTemp_C: '°C',
  shuntTemp_C: '°C',
  tempMax_C: '°C',
  tempMin_C: '°C',
  ahGained_mAh: 'Ah',
  ahSpent_mAh: 'Ah',
  whSpent: 'kWh',
  fullCapacity_mAh: 'Ah',
  remainingCapacity_mAh: 'Ah',
  cellVoltMax_mV: 'V',
  cellVoltMin_mV: 'V',
}


const UNIT_BY_KEY_PATTERN = [
  [/^cell\d+$/, 'V'],
  [/^ntc\d+$/, '°C'],
]


const unitForKey = (key, parsedUnit) =>
  UNIT_BY_KEY[key] ?? UNIT_BY_KEY_PATTERN.find(([re]) => re.test(key))?.[1] ?? parsedUnit

// Plain text for CSV export — always a string/undefined, never a React element.
const formatCellText = (col, row) => {
  if (col.text) return col.text(row)
  const value = row[col.key]
  return value == null ? undefined : value
}

const formatCellValue = (col, row) => {
  if (col.render) return col.render(row)
  return formatCellText(col, row)
}

const MAP_WINDOW = 5000

const AllDataTable = memo(({ points, exportName = 'bms', rangeKey, isLoading = false, error = null, onRetry }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [apiColumns, setApiColumns] = useState([])
  const [visibleKeys, setVisibleKeys] = useState([])
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchColumns = async () => {
      try {
        const { status, data } = await Api.getBmsColumns()
        if (!cancelled && status === 200 && data) {
          const cols = Object.entries(data).map(([key, rawLabel]) => {
            const { label, unit } = parseLabelUnit(rawLabel)
            return { key, label: LABEL_OVERRIDES[key] ?? label, unit: unitForKey(key, unit), width: '190px' }
          })
          setApiColumns(cols)
          setVisibleKeys(cols.map((c) => c.key))
        }
      } catch (error) {
        console.error('Failed to fetch BMS columns:', error)
      }
    }
    fetchColumns()
    return () => { cancelled = true }
  }, [])

  const toggleableColumns = useMemo(
    () => apiColumns.map((c) => ({ ...c, render: RENDER_BY_KEY[c.key], text: TEXT_BY_KEY[c.key] })),
    [apiColumns],
  )
  const columns = [TIMESTAMP_COLUMN, ...toggleableColumns.filter((c) => visibleKeys.includes(c.key))]

  
  const handleCsvExport = async () => {
    if (!points.length || isExporting) return

    const rowCount = points.length
    if (rowCount > CSV_CONFIRM_THRESHOLD) {
      const proceed = window.confirm(
        `This will export ${rowCount.toLocaleString()} rows and may take a while. Continue?`,
      )
      if (!proceed) return
    }

    setIsExporting(true)
    try {
      // Header carries the unit, matching the table — the exported cells are
      // bare values, so dropping it here would lose it entirely.
      const headerLine = columns.map((col) => escapeCsvCell(headerLabel(col))).join(',')
      
      const sortedPoints = [...points].sort((a, b) => b.epochMs - a.epochMs)
      
      let windowStart = -1
      let windowRows = []
      const getLine = (index) => {
        if (index < windowStart || index >= windowStart + windowRows.length) {
          windowStart = index
          windowRows = mapPointsToTableRows(sortedPoints.slice(index, index + MAP_WINDOW))
        }
        const row = windowRows[index - windowStart]
        return columns.map((col) => escapeCsvCell(formatCellText(col, row))).join(',')
      }

      const blob = await buildCsvBlob({ headerLine, rowCount, getLine })
      downloadBlob(blob, `${exportName}-all-data-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`)
    } catch (error) {
      console.error('CSV export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const newestFirst = useMemo(() => [...points].reverse(), [points])
  const totalItems = newestFirst.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const start = (currentPage - 1) * itemsPerPage
  const pagedRows = useMemo(
    () => mapPointsToTableRows(newestFirst.slice(start, start + itemsPerPage)),
    [newestFirst, start, itemsPerPage],
  )

  
  useEffect(() => { setCurrentPage(1) }, [rangeKey])

 
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(Math.max(1, totalPages))
  }, [currentPage, totalPages])

  const isEmpty = totalItems === 0
  
  const isFirstLoad = isLoading && isEmpty
  const showError = !isLoading && isEmpty && Boolean(error)

  return (
    <Card className="gap-0">
      <div className="flex items-center gap-4 pb-3">
        <span className="flex-1 text-sm font-semibold text-[#252a34]">
          All Data
        </span>
        <ColumnSelector
          columns={toggleableColumns}
          visibleKeys={visibleKeys}
          onChange={setVisibleKeys}
        />
        <button
          className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-full disabled:opacity-50"
          onClick={handleCsvExport}
          disabled={!points.length || isExporting}
          title={isExporting ? 'Exporting…' : 'Export CSV'}
        >
          <span className="material-symbols-outlined text-xl text-[#272d37]">
            {isExporting ? 'hourglass_top' : 'file_save'}
          </span>
        </button>
      </div>
      {isFirstLoad ? (
        <div className="flex flex-col min-h-[240px]">
          <Spinner label="Loading data…" />
        </div>
      ) : (
      <div className="relative">
      {isLoading && <LoadingOverlay label="Loading data…" />}
      <div className={`flex flex-col overflow-x-auto transition-opacity ${isLoading ? 'opacity-50 pointer-events-none' : ''}`} aria-busy={isLoading}>
       
        <table className="min-w-full border-collapse table-auto">
          <thead>
            <tr className="bg-[#e8ecf3]">
             
              {columns.map((col) => (
                <th
                  key={col.key}
                  title={headerLabel(col)}
                  style={col.width ? { minWidth: col.width } : undefined}
                  className="px-4 py-2.5 text-left text-sm font-medium text-[#252a34] border-b border-[#dfe4ed] whitespace-nowrap"
                >
                  {headerLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
           
            {pagedRows.map((row, i) => (
                <tr key={i} className="bg-[#f9fafa] border-b border-[#dfe4ed]">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      title={formatCellText(col, row) ?? undefined}
                      className="px-4 py-3 text-sm font-normal text-[#252a34] whitespace-nowrap"
                    >
                      
                      <span className="block max-w-[320px] overflow-hidden text-ellipsis">
                        {formatCellValue(col, row) ?? '--'}
                      </span>
                    </td>
                  ))}
                </tr>
            ))}
          </tbody>
        </table>
        
        {isEmpty && (
        <div className="sticky left-0 flex items-center justify-center px-4 py-10 min-h-[240px]">
          {showError ? (
            <div className="flex flex-col items-center gap-2">
              <span className="material-icons text-2xl text-[#d92d20]">error_outline</span>
              <span className="text-sm font-medium text-[#272d37]">{error}</span>
              {onRetry && (
                <button
                  className="mt-1 h-8 px-3 border border-[#dfe4ed] rounded-md bg-white cursor-pointer text-sm text-[#272d37]"
                  onClick={onRetry}
                >
                  Retry
                </button>
              )}
            </div>
          ) : (
            <span className="text-sm font-normal text-[#616a79]">No data in this time range</span>
          )}
        </div>
      )}
      </div>
      </div>
      )}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
      />
    </Card>
  );
})

export default AllDataTable
